import { computed, Ref } from '@vue/composition-api';
import {
  InterestBehaviorTab,
  InterestBehaviorScene,
  AdgroupFormData,
  INTEREST_LABEL_ID,
  DefaultVideoActionsV2,
  DefaultActionDaysV2,
} from '@tt4b/ttam-enums';
import { getCurrentLang } from '@tt4b/ttam-utils';
import { useInterestBehaviorStore } from './use-interest-behavior-store';
import { useBehaviorData } from './../../../../hooks/creation/adgroup/useBehavior/index';
import { generateId, getTagId, setGameBadgeStorage, getTagScene, trackEvent } from './../../../../utils/index';

// 获取各分类的数据
export function useInterestBehaviorSourceData(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { INTEREST, INMARKET, CREATOR, VIDEO, HASHTAG } = InterestBehaviorScene;
  const { interestBehaviorListMap } = useInterestBehaviorStore();
  function getLabelNodeById(scene, idLists) {
    const labelMap = interestBehaviorListMap.value[scene] || {};
    return idLists.value.map(id => labelMap[id] || { tag_name: id });
  }
  function getLabelNameFormNode(nodes) {
    return nodes.value?.map(node => node?.tag_name) || [];
  }
  const {
    videoCategories,
    creatorCategories,
    hashtagCategories,
    creatorData,
    hashtagData,
    videoData,
    getChangedBehaviorData,
    getClearDataByType,
  } = useBehaviorData({ adgroupData });
  const adTagV2 = computed(() => adgroupData.value.adTagV2);
  const adTagV2NameNode = computed(() => getLabelNodeById(INTEREST, adTagV2));
  const adTagV2Name = computed(() => getLabelNameFormNode(adTagV2NameNode));

  const interestKeywordsI18n = computed(() => adgroupData.value.interestKeywordsI18n);
  const interestKeywordsNameNode = computed(() => getLabelNodeById(INTEREST, interestKeywordsI18n));
  const interestKeywordsName = computed(() => getLabelNameFormNode(interestKeywordsNameNode));

  const inMarketTags = computed(() => adgroupData.value.inMarketTags);
  const inMarketLabelNode = computed(() => getLabelNodeById(INMARKET, inMarketTags));
  const inMarketLabel = computed(() => getLabelNameFormNode(inMarketLabelNode));

  const videoLabelNode = computed(() => getLabelNodeById(VIDEO, videoCategories));
  const videoLabel = computed(() => getLabelNameFormNode(videoLabelNode));
  const creatorLabelNode = computed(() => getLabelNodeById(CREATOR, creatorCategories));
  const creatorLabel = computed(() => getLabelNameFormNode(creatorLabelNode));

  const hashtagLabelNode = computed(() => getLabelNodeById(HASHTAG, hashtagCategories));
  const hashtagLabel = computed(() => getLabelNameFormNode(hashtagLabelNode));
  return {
    creatorData,
    hashtagData,
    videoData,
    getChangedBehaviorData,
    getClearDataByType,
    adTagV2NameNode,
    interestKeywordsNameNode,
    inMarketLabelNode,
    videoLabelNode,
    creatorLabelNode,
    hashtagLabelNode,
    adTagV2,
    inMarketTags,
    interestKeywordsI18n,
    videoCategories,
    creatorCategories,
    hashtagCategories,
    adTagV2Name,
    interestKeywordsName,
    videoLabel,
    creatorLabel,
    hashtagLabel,
    inMarketLabel,
  };
}
export function useInterestBehaviorData(options: { adgroupData: Ref<AdgroupFormData>; emit?: any }) {
  const { adgroupData, emit } = options;
  const { INTEREST, INMARKET, CREATOR, VIDEO, HASHTAG, KEYWORD } = InterestBehaviorScene;

  // id = scene-tag_id
  function translateTagIdToId(scene, idLists) {
    return idLists.value?.map(id => generateId(scene, id)) || [];
  }
  const {
    creatorData,
    hashtagData,
    videoData,
    getChangedBehaviorData,
    getClearDataByType,
    adTagV2NameNode,
    interestKeywordsNameNode,
    inMarketLabelNode,
    videoLabelNode,
    creatorLabelNode,
    hashtagLabelNode,
    adTagV2,
    inMarketTags,
    interestKeywordsI18n,
    videoCategories,
    creatorCategories,
    hashtagCategories,
    adTagV2Name,
    interestKeywordsName,
    videoLabel,
    creatorLabel,
    hashtagLabel,
    inMarketLabel,
  } = useInterestBehaviorSourceData({ adgroupData });
  // 设置选择的tag，将每种类型都重新赋值下，然后通知更新
  function setSelectedTags(nodes) {
    const formDataNodeIds = {};
    nodes.forEach(node => {
      const tagId = Number(getTagId(node));
      const tagScene = getTagScene(node);
      if (formDataNodeIds[tagScene]) {
        formDataNodeIds[tagScene].push(tagId);
      } else {
        formDataNodeIds[tagScene] = [tagId];
      }
    });
    if (formDataNodeIds[VIDEO]?.length) {
      const data = getChangedBehaviorData(
        {
          ...videoData.value,
          categories: formDataNodeIds[VIDEO],
          actions: DefaultVideoActionsV2[VIDEO]().map(item => item.value),
          day: DefaultActionDaysV2[VIDEO].id,
        },
        'video',
      );
      emit?.('change', data);
    } else {
      const data = getClearDataByType('video');
      emit?.('change', data);
    }

    if (formDataNodeIds[CREATOR]?.length) {
      const data = getChangedBehaviorData(
        {
          ...creatorData.value,
          categories: formDataNodeIds[CREATOR],
          actions: DefaultVideoActionsV2[CREATOR]().map(item => item.value),
        },
        'creator',
      );
      emit?.('change', data);
    } else {
      const data = getClearDataByType('creator');
      emit?.('change', data);
    }

    if (formDataNodeIds[HASHTAG]?.length) {
      const data = getChangedBehaviorData({ ...hashtagData.value, categories: formDataNodeIds[HASHTAG] }, 'hashtag');
      emit?.('change', data);
    } else {
      const data = getClearDataByType('hashtag');
      emit?.('change', data);
    }
    const tempData = {
      adTagV2: formDataNodeIds[INTEREST] || [],
      inMarketTags: formDataNodeIds[INMARKET] || [],
      interestKeywordsI18n: formDataNodeIds[KEYWORD] || [],
      interestKeywordsLangI18n: formDataNodeIds[KEYWORD]?.map(node => node.lang || getCurrentLang()) || [],
    };
    emit?.('change', tempData);
  }
  // 所有的tag
  const tagsList = computed(() => {
    // 处理tag的list
    const generateTagsListItem = (scene, data) => ({
      count: data?.length || 0,
      titleKey: InterestBehaviorTab()[scene].subTitleKey,
      selected: data || [],
      scene,
    });
    return [
      generateTagsListItem(INTEREST, [...adTagV2NameNode.value, ...interestKeywordsNameNode.value]),
      generateTagsListItem(INMARKET, inMarketLabelNode.value),
      generateTagsListItem(VIDEO, videoLabelNode.value),
      generateTagsListItem(CREATOR, creatorLabelNode.value),
      generateTagsListItem(HASHTAG, hashtagLabelNode.value),
    ];
  });
  // 选中的tags
  const selectedTags = computed({
    get: () => {
      const tagIds = [
        translateTagIdToId(INTEREST, adTagV2),
        translateTagIdToId(INMARKET, inMarketTags),
        translateTagIdToId(KEYWORD, interestKeywordsI18n),
        translateTagIdToId(VIDEO, videoCategories),
        translateTagIdToId(CREATOR, creatorCategories),
        translateTagIdToId(HASHTAG, hashtagCategories),
      ];

      return tagIds.flat();
    },
    set: val => {
      const gameTagIndex = val.findIndex(id => Number(getTagId(id)) === INTEREST_LABEL_ID.GAME);

      if (gameTagIndex > -1) {
        setGameBadgeStorage(true);
      }
      setSelectedTags(val);
    },
  });
  function onTagClose(node) {
    selectedTags.value = selectedTags.value.filter(id => id !== node.id);
    trackEvent('creat_campaign_targeting_searchbox_tagcross_click', {
      adgroup_id: adgroupData.value.adId || '',
      tag_name: node?.tag_name,
    });
  }
  return {
    selectedTags,
    adTagV2Name,
    interestKeywordsName,
    videoLabel,
    creatorLabel,
    hashtagLabel,
    inMarketLabel,
    tagsList,
    onTagClose,
  };
}
